import { useSearch } from "@tanstack/react-router";
import { MethodPropertyBodySettingDrawer } from "components/Drawers/MethodRequestSettingDrawer/MethodRequestBodySettingDrawer";
import { MethodRequestHeaderSettingDrawer } from "components/Drawers/MethodRequestSettingDrawer/MethodRequestHeaderSettingDrawer";
import { MethodRequestUrlSettingDrawer } from "components/Drawers/MethodRequestSettingDrawer/MethodRequestUrlSettingDrawer";
import { ResponseConditionsSettingDrawer } from "components/Drawers/MethodRequestSettingDrawer/ResponseConditionsSettingDrawer";
import { ResponseFurtherStepSettingDrawer } from "components/Drawers/MethodRequestSettingDrawer/ResponseFurtherStepSettingDrawer";
import { ResponseHowToUseSettingDrawer } from "components/Drawers/MethodRequestSettingDrawer/ResponseHowToUseSettingDrawer";
import { MethodStepSettingDrawer } from "components/Drawers/MethodStepSettingDrawer/MethodStepSettingDrawer";
import { ModifyIntegrationMethodsDrawer } from "components/Drawers/ModifyIntegrationMethodsDrawer/ModifyIntegrationMethodsDrawer";
import { ModifyPostbackIncomingMethodsDrawer } from "components/Drawers/ModifyPostbackIncomingMethodsDrawer/ModifyPostbackIncomingMethodsDrawer.tsx";
import { ModifyPostbackOutgoingMethodsDrawer } from "components/Drawers/ModifyPostbackOutgoingMethodsDrawer/ModifyPostbackOutgoingMethodsDrawer.tsx";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { PermissionEnum, RequestConnectionPropertyType } from "types/enums";
import { DrawerId } from "types/models";

import FormFieldData = App.Data.FormFieldData;
import ResponseConnectionPropertyData = App.Data.ResponseConnectionPropertyData;

interface BodySearchParams {
    [`methodPropertyBodySetting-id`]: string;
    [`methodPropertyBodySetting-parentName`]: string;
    [`methodPropertyBodySetting-propertyType`]: string;
}

interface HeaderSearchParams {
    [`methodPropertyHeaderSetting-id`]: string;
    [`methodPropertyHeaderSetting-parentName`]: string;
}

interface UrlSearchParams {
    [`methodPropertyUrlSetting-id`]: string;
    [`methodPropertyUrlSetting-parentName`]: string;
    [`methodPropertyUrlSetting-propertyType`]: string;
}

interface ResponseConditionsSearchParams {
    [`responseConditionsSetting-methodIndex`]: string;
    [`responseConditionsSetting-responseHandlerIndex`]: string;
    [`responseConditionsSetting-id`]: string;
}

interface FurtherStepSearchParams {
    [`responseFurtherStepSetting-methodIndex`]: string;
    [`responseFurtherStepSetting-responseHandlerIndex`]: string;
    [`responseFurtherStepSetting-id`]: string;
}

interface ResponseHowToUseSearchParams {
    [`responseHowToUseSetting-methodIndex`]: string;
    [`responseHowToUseSetting-responseHandlerIndex`]: string;
    [`responseHowToUseSetting-id`]: string;
}

interface SearchParams {
    [`methodStepSetting-id`]: string;
}

interface EditorDrawersProps {
    leadFields?: Array<FormFieldData | ResponseConnectionPropertyData>;
}

interface ModifyMethodsSearchParams {
    [`modifyIntegrationMethods-id`]: string;
}

interface ModifyIncomingPostbackMethodsSearchParams {
    [`modifyPostbackIncomingMethods-id`]: string;
}

interface ModifyOutgoingPostbackMethodsSearchParams {
    [`modifyPostbackOutgoingMethods-id`]: string;
}

export const EditorDrawers: FC<EditorDrawersProps> = ({ leadFields }) => {
    const bodySearchParams = useSearch({ strict: false }) as BodySearchParams;
    const bodySetting = DrawerId.MethodPropertyBodySetting;

    const headerSearchParams = useSearch({ strict: false }) as HeaderSearchParams;
    const headerSetting = DrawerId.MethodPropertyHeaderSetting;

    const urlSearchParams = useSearch({ strict: false }) as UrlSearchParams;
    const urlSetting = DrawerId.MethodPropertyUrlSetting;

    const responseConditionsSearchParams = useSearch({ strict: false }) as ResponseConditionsSearchParams;
    const responseConditionsSetting = DrawerId.ResponseConditionsSetting;

    const furtherStepSearchParams = useSearch({ strict: false }) as FurtherStepSearchParams;
    const furtherStepSetting = DrawerId.ResponseFurtherStepSetting;

    const ResponseHowToUseSearchParams = useSearch({ strict: false }) as ResponseHowToUseSearchParams;
    const ResponseHowToUseSetting = DrawerId.ResponseHowToUseSetting;

    const searchParams = useSearch({ strict: false }) as SearchParams;
    const stepSetting = DrawerId.MethodStepSetting;

    const modifyMethodsSearchParams = useSearch({ strict: false }) as ModifyMethodsSearchParams;
    const modifyIntegrationMethodsDrawerId = DrawerId.ModifyIntegrationMethods;

    const modifyPostbackIncomingMethodsSearchParams = useSearch({
        strict: false,
    }) as ModifyIncomingPostbackMethodsSearchParams;
    const modifyPostbackIncomingMethodsDrawerId = DrawerId.ModifyPostbackIncomingMethods;

    const modifyPostbackOutgoingMethodsSearchParams = useSearch({
        strict: false,
    }) as ModifyOutgoingPostbackMethodsSearchParams;
    const modifyPostbackOutgoingMethodsDrawerId = DrawerId.ModifyPostbackOutgoingMethods;

    const permissionsUtils = usePermission();

    return (
        <>
            {modifyMethodsSearchParams[`${modifyIntegrationMethodsDrawerId}-id`] &&
                permissionsUtils.hasPermission(PermissionEnum.ToolIntegrationEditorExportView) && (
                    <ModifyIntegrationMethodsDrawer />
                )}

            {modifyPostbackIncomingMethodsSearchParams[`${modifyPostbackIncomingMethodsDrawerId}-id`] &&
                permissionsUtils.hasPermission(PermissionEnum.ToolTrackingIncomingPostbackEditorExportSave) && (
                    <ModifyPostbackIncomingMethodsDrawer />
                )}

            {modifyPostbackOutgoingMethodsSearchParams[`${modifyPostbackOutgoingMethodsDrawerId}-id`] &&
                permissionsUtils.hasPermission(PermissionEnum.ToolTrackingOutgoingPostbackEditorExportSave) && (
                    <ModifyPostbackOutgoingMethodsDrawer />
                )}

            {searchParams[`${stepSetting}-id`] && (
                <MethodStepSettingDrawer name={parseFloat(searchParams[`${stepSetting}-id`])} />
            )}
            {bodySearchParams[`${bodySetting}-id`] && (
                <MethodPropertyBodySettingDrawer
                    name={parseFloat(bodySearchParams[`${bodySetting}-id`])}
                    parentName={parseFloat(bodySearchParams[`${bodySetting}-parentName`])}
                    propertyType={bodySearchParams[`${bodySetting}-propertyType`] as RequestConnectionPropertyType}
                    leadFields={leadFields}
                />
            )}
            {headerSearchParams[`${headerSetting}-id`] && (
                <MethodRequestHeaderSettingDrawer
                    name={parseFloat(headerSearchParams[`${headerSetting}-id`])}
                    parentName={parseFloat(headerSearchParams[`${headerSetting}-parentName`])}
                />
            )}

            {urlSearchParams[`${urlSetting}-id`] && (
                <MethodRequestUrlSettingDrawer
                    name={parseFloat(urlSearchParams[`${urlSetting}-id`])}
                    parentName={parseFloat(urlSearchParams[`${urlSetting}-parentName`])}
                    propertyType={urlSearchParams[`${urlSetting}-propertyType`] as RequestConnectionPropertyType}
                    leadFields={leadFields}
                />
            )}
            {responseConditionsSearchParams[`${responseConditionsSetting}-id`] && (
                <ResponseConditionsSettingDrawer
                    methodIndex={parseFloat(responseConditionsSearchParams[`${responseConditionsSetting}-methodIndex`])}
                    responseHandlerIndex={parseFloat(
                        responseConditionsSearchParams[`${responseConditionsSetting}-responseHandlerIndex`]
                    )}
                    name={parseFloat(responseConditionsSearchParams[`${responseConditionsSetting}-id`])}
                />
            )}
            {furtherStepSearchParams[`${furtherStepSetting}-id`] && (
                <ResponseFurtherStepSettingDrawer
                    methodIndex={parseFloat(furtherStepSearchParams[`${furtherStepSetting}-methodIndex`])}
                    responseHandlerIndex={parseFloat(
                        furtherStepSearchParams[`${furtherStepSetting}-responseHandlerIndex`]
                    )}
                    name={parseFloat(furtherStepSearchParams[`${furtherStepSetting}-id`])}
                />
            )}
            {ResponseHowToUseSearchParams[`${ResponseHowToUseSetting}-id`] && (
                <ResponseHowToUseSettingDrawer
                    methodIndex={parseFloat(ResponseHowToUseSearchParams[`${ResponseHowToUseSetting}-methodIndex`])}
                    responseHandlerIndex={parseFloat(
                        ResponseHowToUseSearchParams[`${ResponseHowToUseSetting}-responseHandlerIndex`]
                    )}
                    name={parseFloat(ResponseHowToUseSearchParams[`${ResponseHowToUseSetting}-id`])}
                />
            )}
        </>
    );
};
